// @flow
import type {Node} from 'react'
import React, {useCallback, useEffect, useState} from 'react'
import {QueryRenderer, graphql} from 'react-relay'
import type {GraphQLTaggedNode} from 'react-relay'

import environment from 'environment'
import logger from 'utils/MiscUtils/main'
import {isSidebarOpen, toggleSidebar} from '../../globalControl'
import OrgSelect from '../../SidebarView/OrgSelect'

import type {
  OrgSelectIslandQueryResponse as Response,
} from './__generated__/OrgSelectIslandQuery.graphql'

// GF-2954: the org switcher is one of the sidebar's surviving React islands,
// mounted inside the server-rendered shell with its own query (per-section
// queries sanctioned by the Frontend Architect as a rule-6 exception on
// GF-2954). Open/close state lives in the `sidebar` Stimulus controller; this
// island reads it through globalControl and re-renders on `sidebar:changed`
// (island-contract rule 3 — events across the boundary, not shared handles).
export const orgSelectIslandQuery: GraphQLTaggedNode = graphql`
  query OrgSelectIslandQuery($orgDatabaseId: String!) {
    organization(databaseId: $orgDatabaseId) {
      ...OrgSelect_organization

      viewer {
        ...OrgSelect_viewer
      }
    }
  }
`

type Props = $ReadOnly<{
  orgDatabaseId: string,
}>

type RendererProps = $ReadOnly<{
  // eslint-disable-next-line react/no-unused-prop-types
  error: ?Object,
  // eslint-disable-next-line react/no-unused-prop-types
  props: ?Response,
  ...
}>

function OrgSelectIsland({orgDatabaseId}: Props): Node {
  const [expanded, setExpanded] = useState(isSidebarOpen())

  useEffect(() => {
    const handleChanged = () => setExpanded(isSidebarOpen())
    window.addEventListener('sidebar:changed', handleChanged)

    return () => window.removeEventListener('sidebar:changed', handleChanged)
  }, [])

  const render = useCallback(({props: response, error}: RendererProps) => {
    if (error) {
      logger.warn('error:', error)
      return null
    }

    const organization = response?.organization
    if (!organization || !organization.viewer)
      return null

    return (
      <OrgSelect
        organization={organization}
        viewer={organization.viewer}
        sideBarExpanded={expanded}
        toggleSidebar={toggleSidebar}
      />
    )
  }, [expanded])

  return (
    <QueryRenderer
      environment={environment}
      fetchPolicy="store-and-network"
      query={orgSelectIslandQuery}
      variables={{orgDatabaseId}}
      render={render}
    />
  )
}

export default OrgSelectIsland
